
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { LessonHeader } from "@/components/courses/LessonHeader";
import { LessonSidebar } from "@/components/courses/LessonSidebar";
import { LessonNavigation } from "@/components/courses/LessonNavigation";
import { useLessonData } from "@/hooks/useLessonData";
import { storeRedirectUrl } from "@/lib/redirectUtils";

export default function CourseLesson({ lang = "en" }: { lang?: string }) {
  // Extract courseSlug, moduleSlug, and lessonSlug from URL parameters
  const { courseSlug = "", moduleSlug = "", lessonSlug = "" } = useParams<{ courseSlug: string; moduleSlug: string; lessonSlug: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const { 
    currentLesson, 
    courseTitle,
    allModules,
    allLessons: allLessonsFromHook,
    content, 
    loading,
    completedLessons,
    markLessonComplete,
    getAdjacentLessons
  } = useLessonData({ courseSlug, moduleSlug, lessonSlug, userId: user?.id });

  // Convert allLessons from Record<string, Lesson[]> to Record<string, Lesson>
  // by flattening the lesson arrays into a single map
  const allLessons = React.useMemo(() => {
    const flatLessons: Record<string, any> = {};
    if (allLessonsFromHook) {
      Object.entries(allLessonsFromHook).forEach(([moduleId, lessons]) => {
        if (Array.isArray(lessons)) {
          lessons.forEach(lesson => {
            flatLessons[lesson.slug] = lesson;
          });
        }
      });
    }
    return flatLessons;
  }, [allLessonsFromHook]);

  // Get adjacent lessons
  const { prev: prevLesson, next: nextLesson } = getAdjacentLessons();
  
  // Check if current lesson is completed
  const isLessonCompleted = currentLesson ? !!completedLessons[currentLesson.slug] : false;

  // Improved auth handling using localStorage as backup and redirect URL storage
  useEffect(() => {
    const checkAuth = () => {
      // Only redirect if we're sure user isn't authenticated
      if (!authLoading && !user && !localStorage.getItem('authUser')) {
        // Store the current URL before redirecting to auth
        storeRedirectUrl(window.location.pathname);
        navigate("/auth");
      }
    };
    
    // Give a slight delay to ensure auth state is properly checked
    const authTimer = setTimeout(checkAuth, 200);
    return () => clearTimeout(authTimer);
  }, [authLoading, user, navigate]);

  if (authLoading || loading) {
    return (
      <div className="container flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Safe guard to prevent flash of unauthenticated content
  if (!user && !localStorage.getItem('authUser')) return null;

  if (!currentLesson) {
    return (
      <div className="container py-12 text-center">
        <h2 className="text-2xl font-bold">Lesson not found</h2>
        <p className="mt-4">The lesson you're looking for doesn't exist or you don't have access to it.</p>
      </div>
    );
  }

  return (
    <div className="container grid grid-cols-12 gap-8 py-12">
      <div className="col-span-12 lg:col-span-3">
        <LessonSidebar 
          courseSlug={courseSlug}
          currentLessonSlug={lessonSlug}
          modules={allModules}
          lessons={allLessons}
          completedLessons={completedLessons}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      <div className="col-span-12 lg:col-span-9">
        <LessonHeader 
          courseSlug={courseSlug}
          courseTitle={courseTitle}
          currentLesson={currentLesson}
          onOpenSidebar={() => setSidebarOpen(true)}
          isCompleted={isLessonCompleted}
          onMarkComplete={markLessonComplete}
        />

        <div 
          className="prose dark:prose-invert max-w-none mb-8"
          dangerouslySetInnerHTML={{ __html: content }}
        />

        <LessonNavigation 
          courseSlug={courseSlug}
          prevLesson={prevLesson} 
          nextLesson={nextLesson}
          isCompleted={isLessonCompleted}
          onMarkComplete={markLessonComplete}
        />
      </div>
    </div>
  );
}
